import { AccessDenied } from '../../routes/AuthRoutes';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Eye, EyeOff, UsersRound, X } from 'lucide-react';
import { listRoles } from './userService';
import Toast from '../../components/Toast';
import useFieldValidation from '../../components/useFieldValidation';
import RoleDropdown from './RoleDropdown';
import UserMobileField from './UserMobileField';
import { composeMobile } from '../../components/CustomerModal';
import '../warehouses/warehouses.css';
import './users.css';

export function limitMobileDigits(value) {
  let digits = 0;
  return value.replace(/\d/g, digit => ++digits <= 10 ? digit : '');
}

export function validateUserDetails(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  else if (form.name.trim().length > 150) errors.name = 'Name must be no more than 150 characters.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) || form.email.trim().length > 255) errors.email = 'Enter a valid email address.';
  if (form.mobile.trim() && !/^\+?[0-9 ()-]{7,50}$/.test(form.mobile.trim())) errors.mobile = 'Enter a valid mobile number.';
  if (form.mobile.replace(/\D/g, '').length > 10) errors.mobile = 'Mobile number must not be greater than 10 digits.';
  if (!form.role_id) errors.role_id = 'Select a role.';
  return errors;
}

export function validateUser(form) {
  const errors = validateUserDetails(form);
  if (form.password.length < 8 || new Blob([form.password]).size > 72) errors.password = 'Password must be at least 8 characters.';
  if (!form.confirmPassword) errors.confirmPassword = 'Confirm Password is required.';
  else if (form.password !== form.confirmPassword) errors.confirmPassword = 'Password and confirm password do not match.';
  return errors;
}

export default function CreateUserPage() {
  return <Navigate to="/users" replace state={{ openCreateUser: true }} />;
}

export function CreateUserModal({ onClose, onSubmit }) {
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [form, setForm] = useState({ name: '', email: '', mobile: '', dialIso: 'IN', role_id: '', password: '', confirmPassword: '', is_active: true });
  const { errors, setErrors, touchField } = useFieldValidation(form, validateUser);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const nameRef = useRef(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => nameRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, []);
  const [forbidden, setForbidden] = useState(false);
  const [toast, setToast] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape' && !submitting.current) onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);
  useEffect(() => {
    let active = true;
    setLoading(true); setToast(null);
    listRoles().then(rows => {
      if (!active) return;
      setRoles(rows);
      const defaultRole = rows.find(role => role.name === 'User');
      setForm(current => ({ ...current, role_id: defaultRole ? String(defaultRole.id) : '' }));
      if (!defaultRole) setToast({ tone: 'error', message: 'The User role is unavailable. Please contact your administrator.' });
    }).catch(error => {
      if (active) { setForbidden(error.status === 403); setToast({ tone: 'error', message: error.message }); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);
  async function submit(event) {
    event.preventDefault();
    if (submitting.current || loading) return;
    const validation = validateUser(form);
    if (Object.keys(validation).length) { setErrors(validation); return; }
    submitting.current = true; setSaving(true);
    try {
      await onSubmit({ ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), mobile: composeMobile(form.dialIso, form.mobile) });
      onClose();
    } catch (error) {
      setForbidden(error.status === 403);
      setErrors({ form: error.message });
      setToast({ tone: 'error', message: error.message });
    } finally { submitting.current = false; setSaving(false); }
  }
  if (forbidden) return <AccessDenied />;
  return <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !submitting.current) onClose(); }}>
    <Toast toast={toast} onClose={() => setToast(null)} />
    <form className="wh-form-modal users-create-modal" role="dialog" aria-modal="true" aria-labelledby="user-create-title" onSubmit={submit} noValidate>
      <header className="wh-form-head">
        <span className="wh-form-head-icon" aria-hidden="true"><UsersRound size={18} strokeWidth={1.8} /></span>
        <div><h2 id="user-create-title">Add User</h2><p>Add a user account to Import ERP</p></div>
        <button className="wh-form-close" type="button" aria-label="Close" onClick={onClose} disabled={saving}><X size={16} strokeWidth={1.8} /></button>
      </header>
      <div className="wh-form-scroll">
      <fieldset disabled={saving} className="users-fields">
        <div className="wh-form-body"><div className="wh-form-grid">
          {[
            ['name', 'Name', 'text', 150], ['email', 'Email', 'email', 255],
          ].map(([name, label, type, maxLength]) => <label className="wh-form-field" key={name} htmlFor={`user-${name}`}>
            <span>{label}{name !== 'mobile' && <em> *</em>}</span>
            <span className={`wh-form-control${errors[name] ? ' invalid' : ''}`}><input ref={name === 'name' ? nameRef : undefined} autoComplete="off" autoFocus={name === 'name'} id={`user-${name}`} type={type} placeholder={name === 'email' ? 'Enter email address' : 'Enter name'} maxLength={maxLength} value={form[name]} onChange={event => setForm(current => ({ ...current, [name]: name === 'mobile' ? limitMobileDigits(event.target.value) : event.target.value }))} onBlur={() => touchField(name)} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `user-${name}-error` : undefined} /></span>
            {errors[name] && <small id={`user-${name}-error`} className="wh-form-error">{errors[name]}</small>}
          </label>)}
          <UserMobileField id="user-mobile" value={form.mobile} iso={form.dialIso} error={errors.mobile} onChange={value => { setForm(current => ({ ...current, mobile: value })); setErrors(current => ({ ...current, form: undefined, mobile: undefined })); }} onCountryChange={iso => { setForm(current => ({ ...current, dialIso: iso })); setErrors(current => ({ ...current, form: undefined, mobile: undefined })); }} onBlur={() => touchField('mobile')} />
          <div className="wh-form-field"><span id="user-role-label">Role <em>*</em></span>
            <RoleDropdown disabled id="user-role" roles={roles} value={form.role_id} loading={loading} error={errors.role_id} onChange={value => setForm(current => ({ ...current, role_id: value }))} onBlur={() => touchField('role_id')} />
            {errors.role_id && <small id="user-role-error" className="wh-form-error">{errors.role_id}</small>}
            {!loading && !roles.length && <button type="button" className="wh-form-cancel" onClick={() => setAttempt(value => value + 1)}>Retry loading roles</button>}
          </div>
          {['password', 'confirmPassword'].map(name => <label className="wh-form-field" key={name} htmlFor={`user-${name}`}>
            <span>{name === 'password' ? 'Password' : 'Confirm Password'} <em>*</em></span>
            <span className={`wh-form-control${errors[name] ? ' invalid' : ''}`}><input id={`user-${name}`} type={visiblePasswords[name] ? 'text' : 'password'} placeholder={name === 'password' ? 'Enter password' : 'Enter confirm password'} autoComplete="new-password" value={form[name]} onChange={event => setForm(current => ({ ...current, [name]: event.target.value }))} onBlur={() => touchField(name)} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `user-${name}-error` : undefined} /><button className="users-password-toggle" type="button" aria-label={`${visiblePasswords[name] ? 'Hide' : 'Show'} ${name === 'password' ? 'password' : 'confirm password'}`} aria-pressed={Boolean(visiblePasswords[name])} onClick={() => setVisiblePasswords(current => ({ ...current, [name]: !current[name] }))}>{visiblePasswords[name] ? <EyeOff size={16} /> : <Eye size={16} />}</button></span>
            {errors[name] && <small id={`user-${name}-error`} className="wh-form-error">{errors[name]}</small>}
          </label>)}
          <label className="users-active"><input type="checkbox" checked={form.is_active} onChange={event => setForm(current => ({ ...current, is_active: event.target.checked }))} />Active</label>
        </div>{errors.form && <p className="wh-modal-error" role="alert">{errors.form}</p>}</div>
      </fieldset>
      </div>
      <footer className="wh-form-footer"><button className="wh-form-cancel" type="button" onClick={onClose} disabled={saving}>Cancel</button><button className="wh-form-save" type="submit" disabled={saving || loading || !roles.length}>{saving ? 'Creating...' : <><Check size={15} strokeWidth={2.2} />Create User</>}</button></footer>
    </form>
  </div>;
}
